<template>
  <div
    :class="[
      'sonar-level-badge',
      isResultsModal && 'sonar-level-badge--no-links',
      badge.status && `sonar-level-badge${badge.status}`,
    ]"
    @click="startAssessment()"
  >
    <v-popover
      :trigger="'hover'"
      :popoverInnerClass="'sonar-level-badge__popover--inner'"
    >
      <div
        :class="[
          `hexagon sonar-level-badge__hexagon-wrapper`,
          badge.status && `sonar-level-badge__hexagon-wrapper${badge.status}`,
          level.difficulty &&
            `sonar-level-badge__hexagon-wrapper--${level.difficulty}`,
        ]"
      >
        <div
          :class="[
            `hexagon sonar-level-badge__hexagon`,
            badge.status && `sonar-level-badge__hexagon${badge.status}`,
            level.difficulty &&
              `sonar-level-badge__hexagon--${level.difficulty}`,
          ]"
        >
          <component
            :is="badge.icon"
            v-if="badge.icon"
            :class="[
              `sonar-level-badge__icon sonar-level-badge__icon${badge.status}`,
            ]"
          />
        </div>
      </div>

      <template slot="popover">
        <div class="sonar-level-badge__popover">
          <div class="sonar-level-badge__popover--level-container">
            <div class="sonar-level-badge__popover--level-difficulty-wrapper">
              <Difficulty :difficulty="level.difficulty" :colored="true" />
            </div>
            <div
              :class="[
                'sonar-level-badge__popover--level-pill-wrapper',
                `sonar-level-badge__popover--level-pill-wrapper${badge.status}`,
              ]"
            >
              <div
                :class="[
                  'sonar-level-badge__popover--level-pill',
                  `sonar-level-badge__popover--level-pill${badge.status}`,
                ]"
              >
                {{ badge.tooltip }}
              </div>
            </div>
          </div>
          <div class="sonar-level-badge__popover--line" />
          <div
            class="sonar-level-badge__popover--description"
            v-html="badge.description"
          />
        </div>
      </template>
      <img :src="levelHover" class="sonar-level-badge__hover" />
    </v-popover>
  </div>
</template>

<script>
import { formatDistanceToNowStrict, formatDistanceStrict } from 'date-fns'
import { CheckIcon, LockIcon, UnlockIcon, ClockIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import { hoverLevelMap } from '@/sonar/helpers/utils'
import { capitalize } from '@/helpers/textTransform'
import Difficulty from '@/components/properties/Difficulty.vue'

export default {
  name: 'SonarLevelBadge',
  components: {
    CheckIcon,
    LockIcon,
    UnlockIcon,
    Difficulty,
  },
  props: {
    level: {
      type: Object,
      required: true,
    },
    isResultsModal: {
      type: Boolean,
      required: false,
    },
    blockedByCoolingOff: {
      type: Boolean,
      required: false,
    },
    previousLevelName: {
      type: String,
      required: false,
      default: null,
    },
    coolingOffTime: {
      type: Number,
      required: false,
      default: 0,
    },
    takenDate: {
      type: String,
      required: false,
      default: null,
    },
    timerText: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      badge: {
        icon: null,
        status: '',
        tooltip: '',
        description: '',
        coolingOff: false,
      },
    }
  },
  computed: {
    levelHover() {
      return hoverLevelMap(this.level.difficulty)
    },
    isDifficultyComingSoon() {
      const coming_soon = CONSTANTS.SONAR.ASSESSMENT.CONTENT_STATUS.COMING_SOON
      const comingSoon = coming_soon.replace('_', ' ')
      return (
        this.level.status ===
          CONSTANTS.SONAR.ASSESSMENT.LEVEL_STATUS.UNLOCKED &&
        (this.level.assessment_status === coming_soon ||
          this.level.assessment_status === comingSoon)
      )
    },
  },
  watch: {
    timerText() {
      this.setBadgeData()
    },
  },
  mounted() {
    this.setBadgeData()
  },
  beforeUpdate() {
    this.setBadgeData()
  },
  methods: {
    startAssessment() {
      const { is_current } = this.level
      if (this.isResultsModal || (this.blockedByCoolingOff && is_current))
        return
      const canStartAssessment =
        this.level.status !== CONSTANTS.SONAR.ASSESSMENT.LEVEL_STATUS.LOCKED &&
        !this.isDifficultyComingSoon
      if (canStartAssessment)
        this.$emit('start-assessment', this.level.difficulty)
    },
    formatMinutesToText(minutes) {
      const seconds = minutes * 60
      const duration = formatDistanceStrict(0, seconds * 1000, {
        includeSeconds: true,
      })
      return duration
    },
    getCoolingOffDescription() {
      return `<p>
        Cooling Off Period:  ${this.formatMinutesToText(this.coolingOffTime)}
      <br/>
      Assessment Taken: ${formatDistanceToNowStrict(
        new Date(this.takenDate)
      )} ago
        <br/>
      Retake Available In: ${this.timerText}
        </p>`
    },
    setBadgeData() {
      const { UNLOCKED, LOCKED } = CONSTANTS.SONAR.ASSESSMENT.LEVEL_STATUS
      const { status, is_current } = this.level

      if (this.blockedByCoolingOff && is_current) {
        return (this.badge = {
          icon: ClockIcon,
          status: '--unlocked',
          tooltip: 'Cooling off',
          description: this.getCoolingOffDescription(),
        })
      }

      if (this.isDifficultyComingSoon) {
        return (this.badge = {
          icon: LockIcon,
          status: '--coming-soon',
          tooltip: 'Coming soon',
          description:
            'This level is scheduled to be released soon!  Stay tuned for updates.',
        })
      }
      if (status === UNLOCKED) {
        return (this.badge = {
          icon: UnlockIcon,
          status: '--unlocked',
          tooltip: 'Unlocked',
          description: 'Take this assessment to put your skills to the test.',
        })
      }
      if (status === LOCKED) {
        return (this.badge = {
          icon: LockIcon,
          status: '--locked',
          tooltip: 'Locked',
          description: `<p>Unlock this level by demonstrating your proficiency in the <strong>${capitalize(
            this.previousLevelName
          )}</strong> level assessment.</p>`,
        })
      }
      return (this.badge = {
        icon: CheckIcon,
        status: '--passed',
        tooltip: `Passed`,
        description: `<p><strong>You passed this level!</strong> Retake this assessment to see how you've progressed.</p>`,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
$hover: 9.5px;
$hw: 26px;
$hh: 30px;
$hwHover: $hw + $hover - 2;
$hhHover: $hh + $hover;
$HW: 33px;
$HH: 37px;
$HWHover: $HW + $hover;
$HHHover: $HH + $hover + 1.5;
.sonar-level-badge {
  position: relative;
  cursor: pointer;
  &:active {
    filter: brightness(80%);
  }
  &--locked {
    cursor: not-allowed;
    &:active {
      filter: none;
    }
  }
  &--coming-soon {
    cursor: not-allowed;
    &:active {
      filter: none;
    }
  }
  .v-popover {
    height: auto !important;
    padding-top: 5px !important;
  }
  &__hexagon-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: $hw;
    height: $hh;
    &--novice {
      background: var(--sonar-gradient-level-novice);
    }
    &--professional {
      background: var(--sonar-gradient-level-professional);
    }
    &--advanced {
      background: var(--sonar-gradient-level-advanced);
    }
    &--locked {
      background: var(--sonar-gradient-level-locked);
    }
    &--coming-soon {
      background: var(--sonar-gradient-level-coming-soon);
    }
    &--unlocked {
      width: $HW;
      height: $HH;
    }
  }
  &__hexagon {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24.5px;
    height: 28.5px;
    &--novice {
      background: var(--sonar-gradient-border-level-novice);
    }
    &--professional {
      background: var(--sonar-gradient-border-level-professional);
    }
    &--advanced {
      background: var(--sonar-gradient-border-level-advanced);
    }
    &--locked {
      background: var(--flat-black);
    }
    &--coming-soon {
      background: var(--flat-black);
    }
    &--unlocked {
      width: 30px;
      height: 34.5px;
    }
  }
  &__icon {
    position: absolute;
    color: var(--white);
    width: 12px;
    &--locked {
      color: var(--perfect-gray);
    }
    &--coming-soon {
      color: var(--perfect-gray);
    }
  }
  &__hover {
    position: absolute;
    display: none;
  }
  &--unlocked:hover &__hover {
    position: absolute;
    display: block;
    width: $HWHover;
    height: $HHHover;
    left: -5px;
    top: -0.5px;
  }
  &--passed:hover &__hover {
    position: absolute;
    display: block;
    width: $hwHover;
    height: $hhHover;
    left: -4px;
    top: 0.5px;
  }
  &--locked &__hover {
    display: none;
  }
  &__tooltip {
    text-transform: capitalize !important;
  }
  &__pophover {
    width: 242px;
    height: 92px;
  }
  &--no-links {
    cursor: initial;
    &:hover {
      .sonar-level-badge__hover {
        display: none;
      }
    }
    &:active {
      filter: none;
    }
  }
  &__popover {
    padding: 16px;
    width: 275px;
    min-height: 130px;
    background-color: var(--light-gray-darker);
    border-radius: 4px;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);

    &--inner {
      padding: 0 !important;
    }
    &--level {
      text-transform: capitalize;
      color: var(--white);
      font-family: var(--font-bold);
      font-size: rem(18);
      font-style: normal;
      font-weight: 500;
      line-height: 24px;
    }
    &--line {
      width: 100%;
      height: 1px;
      background: var(--menu-subcategory);
      margin: 14px 0;
    }
    &--description {
      color: var(--white);
      font-family: var(--font-book);
      font-size: rem(14);
      font-weight: 450;
      line-height: 20px;
    }
    &--level-container {
      display: flex;
      justify-content: space-between;
    }

    &--level-pill-wrapper {
      height: 25px;
      width: auto;
      padding: 0;
      position: relative;
      border-radius: 40px;
      background: var(--sonar-gradient-border-assessment-status-pill);
    }

    &--level-pill {
      display: flex;
      align-items: center;
      justify-content: center;
      width: auto;
      height: 25px;
      position: relative;
      padding: 5px 11px;
      box-sizing: border-box;

      $border: 1px;
      color: var(--white);
      background: var(--glass-mid-gray-solid);
      background-clip: padding-box;
      border: solid $border transparent;
      border-radius: 40px;

      &--passed {
        background: var(--positive-dark);
        background-clip: padding-box;
      }
      &--locked {
        background: var(--primary-ine-dark-orange);
        background-clip: padding-box;
      }
      &--coming-soon {
        background: var(--glass-mid-gray-solid);
        background-clip: padding-box;
      }
    }
    &--level-difficulty-wrapper {
      width: auto;
    }
  }
}
</style>
